import {makeStyles} from "tss-react/mui";
import {Box, Breadcrumbs, Button, ButtonProps, CircularProgress, Typography} from "@mui/material";
import {IEntity} from "@buildwitt/shapeshift-types";
import {EntityIdentifier} from "@buildwitt/shapeshift-types/entityService";
import AddIcon from "@mui/icons-material/Add";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import {Link} from "react-router-dom";

const useStyles = makeStyles()((theme) => ({
	header: {
		display: "flex",
		flexDirection: "column"
		// justifyContent: "space-between"
	},
	title: {
		fontSize: "34px",
		lineHeight: 1.24
	},
	newTitle: {
		lineHeight: 1.24,
		textTransform: "capitalize",
		fontSize: "34px"
	},
	subtitle: {
		fontWeight: 400,
		fontSize: "16px"
	},
	breadcrumbs: {
		".MuiBreadcrumbs-li": {
			display: "flex",
			flexDirection: "row",
			alignItems: "center"
		},
		".MuiSvgIcon-root": {
			fontSize: "1.1em",
			marginRight: theme.spacing(0.5)
		}
	},
	titleContainer: {
		display: "flex",
		flexDirection: "row",
		gap: theme.spacing(1),
		marginTop: theme.spacing(1),
		marginBottom: theme.spacing(4)
	},
	draft: {
		padding: theme.spacing(0.75, 1.25),
		borderRadius: "100px",
		color: theme.palette.primary.main,
		border: `1px solid ${theme.palette.primary.main}`
	},
	published: {
		padding: theme.spacing(0.75, 1.25),
		borderRadius: "100px",
		backgroundColor: theme.palette.success.main
	},
	scheduled: {
		padding: theme.spacing(0.75, 1.25),
		borderRadius: "100px",
		backgroundColor: theme.palette.warning.main,
		color: theme.palette.warning.contrastText
	},
	inactive: {
		padding: theme.spacing(0.75, 1.25),
		borderRadius: "100px",
		color: theme.palette.primary.main,
		border: `1px solid ${theme.palette.primary.main}`
	},
	statusContainer: {
		marginLeft: "auto",
		flexShrink: 0,
		display: "flex",
		flexDirection: "row",
		alignItems: "center",
		gap: theme.spacing(1)
	}
}));

export interface IProps<T extends IEntity & {title?: string}, K extends EntityIdentifier> {
	title: string,
	subtitle: string,
	actionButtons?: (null | ({label: string, loading?: boolean} & Omit<ButtonProps, "label" | "children">))[],
	editMode: "edit" | "create",
	entity?: T,
	type?: K,
	rootBreadcrumb: JSX.Element,
	rootSlug: string,
	isPublished?: boolean;
	isScheduled?: boolean;
	isInactive?: boolean;
}

export const AdminHeader = <T extends IEntity & {title?: string}, K extends EntityIdentifier>({
	rootBreadcrumb,
	editMode,
	entity,
	type,
	rootSlug,
	title,
	actionButtons,
	isPublished: isPublishedProp,
	isScheduled = false,
	isInactive = false
}: IProps<T, K>): JSX.Element => {
	const {classes} = useStyles();
	const isPublished = isPublishedProp ?? (!!entity && !!entity.publishedAt);
	const filteredActionButtons = (actionButtons ?? []).filter(Boolean) as NonNullable<IProps<T, K>["actionButtons"]>;
	const showActions = filteredActionButtons.length > 0;
	const showStatusBadge = editMode === "edit" && !!entity;
	const createHeading = editMode === "create" ? title?.trim() || `Create New ${type}` : "";

	return (
		<Box className={classes.header}>
			<Breadcrumbs className={classes.breadcrumbs} separator="/" aria-label="breadcrumb">
				<Box component="span" sx={{display: "flex", alignItems: "center"}}>
					{rootBreadcrumb}
				</Box>
				{editMode === "create" ? (
					<Box component="span" sx={{display: "flex", alignItems: "center"}}>
						<AddIcon />
						<Typography>Create New</Typography>
					</Box>
				) : entity ? (
					<Box component="span" sx={{display: "flex", alignItems: "center"}}>
						{type === "course" ? <DescriptionOutlinedIcon /> : <PlayArrowIcon />}
						<Link to={`${rootSlug}/${entity?.id}`}><Typography>{entity?.title}</Typography></Link>
					</Box>
				) : null}
			</Breadcrumbs>
			<Box className={classes.titleContainer}>
				<Box>
					{editMode === "create" ? (
						<Typography
							variant="h4"
							className={title?.trim() ? classes.title : classes.newTitle}
						>
							{createHeading}
						</Typography>
					) : (
						<Typography variant="h4" className={classes.title}>{entity?.title}</Typography>
					)}
				</Box>
				{showActions || showStatusBadge ? (
					<Box className={classes.statusContainer}>
						{showStatusBadge ? (
							isScheduled ? (
								<Typography variant="small" className={classes.scheduled}>
									Scheduled
								</Typography>
							) : isPublished ? (
								<Typography variant="small" className={classes.published}>
									Published
								</Typography>
							) : isInactive ? (
								<Typography variant="small" className={classes.inactive}>
									Inactive
								</Typography>
							) : (
								<Typography variant="small" className={classes.draft}>
									Draft
								</Typography>
							)
						) : null}
						{filteredActionButtons.map((btn, i) => {
							const {label, loading, ...buttonProps} = btn as {
								label: string;
								loading?: boolean;
							} & Omit<ButtonProps, "children">;
							return (
								<Button key={`action-${i}`} {...buttonProps}>
									{loading ? <CircularProgress size={20} color="inherit" /> : label}
								</Button>
							);
						})}
					</Box>
				) : null}
			</Box>
		</Box>
	);
}
